import React, { useState, useEffect, useReducer, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, collection, query, where, orderBy, getDocs, onSnapshot } from 'firebase/firestore';
import { notify } from '../../utils/notify.jsx';
import PreviewContent from './PreviewContent';
import MessengerScrollablePreview from './MessengerScrollablePreview';
import PreviewToolbar from './PreviewToolbar';
import PaginationControls from './PaginationControls';
import ConfirmationModal from '../ConfirmationModal';
import { extractDesignContent } from './extractDesignContent';
import { extractStoryProgressContent } from './extractStoryProgressContent';

// Note: window.currentBookData is a global variable set by other components
// and is used for storing the latest book data for preview purposes

// GLOBAL FLAGS to control refresh behavior
// NOTE: We're overriding DISABLE_ALL_AUTO_REFRESH to false to ensure content updates
const DISABLE_ALL_AUTO_REFRESH = false; // Changed to false to allow auto-updates
const ALLOW_ONE_TIME_INITIAL_LOAD = true; // Allow a single initial load when component mounts

// Show these storyProgress statuses in the preview list (exclude pending, cancelled, failed, error)
const VALID_PREVIEW_STATUSES = ['completed', 'almostThere', 'outlineGenerated'];

export default function PreviewSection({ 
  currentPage, 
  totalPages, 
  pageContents, 
  onContentChange, 
  onSavePreview, 
  onSaveBasicInfo,
  onContinueWithoutSave,
  onPageChange,
  saveDisabled = false, 
  saveText = 'Save', 
  step = 1,
  bookId = null,
  isProcessing = false,
  operationType = null,
  draftedResult = null
}) {
  // Add state to control debug info visibility
  const [showDebugInfo, setShowDebugInfo] = useState(() => {
    // Use localStorage to check if debug tools should be shown
    return localStorage.getItem('showDebugTools') === 'true';
  });
  
  // UI state
  const [forceRerender, setForceRerender] = useState(0);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [isLoadingPreviews, setIsLoadingPreviews] = useState(false);
  const [forceRenderTimeout, setForceRenderTimeout] = useState(false);
  
  // Data state
  const [availablePreviews, setAvailablePreviews] = useState([]);
  const [previewIds, setPreviewIds] = useState([]);
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);
  const [localPageContents, setLocalPageContents] = useState(() => {
    // Initialize with empty strings array matching totalPages length
    const emptyContents = new Array(totalPages).fill('');
    return pageContents || emptyContents;
  });
  
  // Manual refresh button state
  const [showRefreshButton, setShowRefreshButton] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // References
  const initialLoadRef = useRef(false);
  const contentLoadAttemptRef = useRef({});
  const unknownStateRefreshRef = useRef(false);
  const firestoreListenerRef = useRef(null);
  const designVersionShownRef = useRef(false); // Track when design version is deliberately shown
  
  // Location info
  const location = useLocation();
  const navigate = useNavigate();
  const isEditMode = location.pathname.startsWith('/edit');
  
  // Force UI to rerender when state changes
  const [ignored, forceUpdate] = useReducer(x => x + 1, 0);
  
  // Handle content change for individual pages
  const handleContentChange = useCallback((pageIndex, newContent) => {
    console.log('🔍 CHANGE DEBUG: Page', pageIndex, 'newContent length:', Array.isArray(newContent) ? newContent.length : 'not array');
    if (Array.isArray(newContent)) {
      console.log('🔍 CHANGE DEBUG: Element IDs:', newContent.map(el => el.id));
    }
    
    // Create a new array with the updated content
    const updatedContents = [...localPageContents];
    
    // Check if newContent is an array of elements (design mode) vs simple content (story mode)
    const isElementArray = Array.isArray(newContent) && newContent.length > 0 && newContent[0].id;
    
    if (isElementArray) {
      // Design mode: merge element changes instead of replacing entire page
      const existingContent = updatedContents[pageIndex] || [];
      if (Array.isArray(existingContent)) {
        // Merge: update only the element that changed, preserve others
        updatedContents[pageIndex] = existingContent.map(existingEl => {
          const updatedEl = newContent.find(newEl => newEl.id === existingEl.id);
          return updatedEl || existingEl;
        });
        // Add any new elements that weren't in the existing content
        newContent.forEach(newEl => {
          if (!existingContent.some(el => el.id === newEl.id)) {
            updatedContents[pageIndex].push(newEl);
          }
        });
      } else {
        // Fallback: replace if existing content is not structured
        updatedContents[pageIndex] = newContent;
      }
    } else {
      // Story mode: store as received (strings)
      updatedContents[pageIndex] = newContent;
    }
    
    // Update local state
    setLocalPageContents(updatedContents);
    
    // Notify parent component
    if (typeof onContentChange === 'function') {
      onContentChange(updatedContents);
    }
  }, [localPageContents, onContentChange]);

  // Handle content changes for all pages at once (for Generate Preview)
  const handleAllContentChange = useCallback((newContent) => {
    console.log('📝 All content changed (via Generate Preview)');
    
    // Check if we're currently in design mode
    const isInDesignMode = designVersionShownRef.current === true;
    console.log(`💾 GENERATE PREVIEW: Current design mode = ${isInDesignMode}`);
    
    // Debug the newContent to understand its type
    console.log('CONTENT RECEIVED:', { 
      type: typeof newContent, 
      isArray: Array.isArray(newContent),
      value: newContent
    });
    
    // DEFENSIVE: Make sure we always store an array in state
    const safeContent = Array.isArray(newContent) ? newContent : Array(totalPages).fill('');
    
    // Apply content changes with safe array
    setLocalPageContents(safeContent);
    
    // Call parent handler if provided
    if (typeof onContentChange === 'function') {
      onContentChange(safeContent); // Pass the safe array to parent too
    }
    
    // CRITICAL: Preserve design mode state when content changes
    if (isInDesignMode) {
      console.log('🛠 PRESERVING DESIGN MODE during Generate Preview');
      
      // Small timeout to ensure state updates happen in correct order
      setTimeout(() => {
        // Forcefully reset design mode flags to ensure consistency
        designVersionShownRef.current = true;
        setCurrentPreviewIndex(previewIds.length);
        console.log('💯 DESIGN MODE PRESERVED');
      }, 10);
    }
    
    // Force a rerender to ensure UI is consistent
    setForceRerender(prev => prev + 1);
  }, [onContentChange, previewIds.length]);

  // Process a storyProgress document and extract content
  const processStoryProgressDocument = useCallback((doc, source) => {
    console.log('Processing storyProgress document for display, source:', source);
    if (!doc) {
      console.error('❌ Invalid document passed to processStoryProgressDocument');
      return;
    }
    
    try {
      const data = doc.data();
      
      console.log(`📚 Raw storyProgress document (${source}):`, {
        id: doc.id,
        bookId: data.bookId,
        status: data.status,
        createdAt: data.createdAt?.toDate?.() || 'unknown',
        resultType: typeof data.result,
        resultLength: typeof data.result === 'string' ? data.result.length : 'N/A'
      });
      
      // Parse result if it's a string
      let resultData = data.result;
      if (typeof resultData === 'string') {
        try {
          resultData = JSON.parse(resultData);
          console.log('📄 Parsed string result into object:', resultData);
        } catch (e) {
          console.error('❌ Failed to parse result string:', e);
          if (typeof data.result === 'string') {
            console.log('🔎 First 100 chars of result:', data.result.substring(0, 100));
          }
        }
      } else if (resultData === null || resultData === undefined) {
        console.error('❌ Result data is null or undefined');
        console.log('🔎 All available fields in data:', Object.keys(data));
      }
      
      // Extract storyProgress content - TEXT ONLY, NO DESIGN INFLUENCE
      const newContent = extractStoryProgressContent(resultData, totalPages);
      
      // If we found content, apply it
      if (newContent.some(c => c)) {
        console.log('🎉 Setting local page contents with content from storyProgress');
        
        // CRITICAL: Preserve design mode if active during content updates
        // This ensures Generate Preview doesn't break design mode
        const isInDesignMode = designVersionShownRef.current === true;
        console.log(`📝 PROCESS DOCUMENT: Current design mode=${isInDesignMode}`);
        
        // Apply content change
        setLocalPageContents(newContent);
        if (typeof onContentChange === 'function') {
          onContentChange(newContent);
        }
        
        // If we're in design mode, ensure we stay there after content updates
        // This prevents Generate Preview from resetting to storyProgress
        if (isInDesignMode) {
          console.log('💯 PRESERVING DESIGN MODE during storyProgress update');
          // Add a small timeout to ensure the design mode stays set after state updates
          setTimeout(() => {
            designVersionShownRef.current = true;
            setCurrentPreviewIndex(previewIds.length);
          }, 10);
        }
        
        // Force component rerender
        setForceRerender(prev => prev + 1);
        setShowRefreshButton(false);
      } else {
        console.log('⚠️ No content found in storyProgress document');
        setShowRefreshButton(true);
      }
    } catch (err) {
      console.error('❌ Error processing storyProgress document:', err);
      setShowRefreshButton(true);
    }
  }, [totalPages, onContentChange]);

  // Check for existing design content in the book
  const hasDesignContent = useCallback(() => {
    // @ts-ignore - window.currentBookData is defined in the global scope
    const book = window.currentBookData || {};
    
    // Check if book has valid spreads with elements
    if (!book.spreads || Object.keys(book.spreads).length === 0) {
      console.log('📐 No spreads found in book');
      return false;
    }
    
    // Check if any spread has text elements
    let hasContent = false;
    
    for (const spreadId of Object.keys(book.spreads)) {
      // Skip cover for this check
      if (spreadId === 'spread-0') continue;
      
      const elements = book.spreads[spreadId]?.elements || [];
      
      // Check if this spread has any text elements with content
      for (const element of elements) {
        if (element?.type === 'text' && typeof element.content === 'string' && element.content.trim().length > 0) {
          hasContent = true;
          console.log(`📐 Found design content in spread ${spreadId}`);
          break;
        }
      }
      
      if (hasContent) break;
    }
    
    if (!hasContent) {
      console.log('📐 No design content found in spreads');
    }
    
    return hasContent;
  }, []);

  // Function to show the current design version instead of a preview
  const showDesignVersion = useCallback(() => {
    console.log('🔄 Attempting to show design version');
    setIsLoadingPreviews(true);
    setIsClearing(true);
    
    // CRITICAL: Set flag that we're showing design version
    // This flag drives all the display logic
    designVersionShownRef.current = true;
    
    try {
      // CRITICAL: Immediately set currentPreviewIndex to indicate design version
      // This must happen before any async operations to ensure consistent UI
      setCurrentPreviewIndex(previewIds.length);
      console.log('🔄 Design version display process starting');

      // Clear content temporarily
      const emptyContents = Array(totalPages).fill("");
      setLocalPageContents(emptyContents);
      
      // Set current index to be after all previews to indicate design version
      setCurrentPreviewIndex(previewIds.length);
      
      // @ts-ignore - window.currentBookData is defined in the global scope
      const book = window.currentBookData || {};
      const newContent = Array(totalPages).fill('');
      
      // Extract content from book spreads
      if (book.spreads && Object.keys(book.spreads).length > 0) {
        for (const spreadId of Object.keys(book.spreads)) {
          // Skip cover
          if (spreadId === 'spread-0') continue;
          
          const spreadNum = parseInt(spreadId.split('-')[1]);
          const elements = book.spreads[spreadId]?.elements || [];
          
          // Find text and image elements
          for (const element of elements) {
            if (element?.type === 'text' && typeof element.content === 'string' && element.content.trim().length > 0) {
              // Use pageNumber if available, otherwise derive from spread number
              const pageIndex = (element.pageNumber ? element.pageNumber - 1 : spreadNum - 1);
              
              if (pageIndex >= 0 && pageIndex < totalPages) {
                // Store as array of elements instead of concatenated string
                if (!Array.isArray(newContent[pageIndex])) {
                  newContent[pageIndex] = newContent[pageIndex] ? [{ content: newContent[pageIndex] }] : [];
                }
                newContent[pageIndex].push({ content: element.content });
              }
            } else if (element?.type === 'image') {
              // Handle image elements
              const pageIndex = (element.pageNumber ? element.pageNumber - 1 : spreadNum - 1);
              
              if (pageIndex >= 0 && pageIndex < totalPages) {
                // Initialize page content if needed
                if (!Array.isArray(newContent[pageIndex])) {
                  newContent[pageIndex] = [];
                }
                // Add image element
                newContent[pageIndex].push({ type: 'image', src: element.src, imagePrompt: element.imagePrompt });
              }
            }
          }
        }
      }
      
      // Apply the content
      if (newContent.some(c => c)) {
        setLocalPageContents(newContent);
        if (typeof onContentChange === 'function') {
          onContentChange(newContent);
        }
        notify.info('Showing current design', { toastId: 'preview-show-design' });
      } else {
        notify.warning('No design content found', { toastId: 'preview-no-design' });
      }
    } catch (error) {
      console.error('Error showing design version:', error);
      notify.error('Failed to show design', { toastId: 'preview-show-design-error' });
    } finally {
      setIsLoadingPreviews(false);
      setIsClearing(false);
    }
  }, [previewIds.length, totalPages, onContentChange]);

  // Handle save click - redirects to design page
  const handleSaveClick = () => {
    if (isEditMode) {
      // Check if we're in design mode - if so, navigate directly to /design
      if (designVersionShownRef.current) {
        console.log('🎨 In design mode - navigating directly to /design');
        navigate(`/design/${bookId}`);
        return;
      }
      
      console.log('In edit mode, showing confirmation modal');
      setShowConfirmModal(true);
    } else {
      // Determine if we are currently viewing the design version
      const isOnDesignVersion = currentPreviewIndex >= previewIds.length && hasDesignContent();
      
      console.log('🔄 SAVE PREVIEW - Starting save operation, isDesignMode:', isOnDesignVersion);
      notify.info('Saving preview... (auto-refresh disabled)', { toastId: 'preview-save' });
      
      // Pass localPageContents AND the design mode flag to onSavePreview
      // The parent (useEditPage) will use isDesignMode to decide which save flow to use
      console.log('Sending localPageContents to onSavePreview:', localPageContents);
      onSavePreview(localPageContents, isOnDesignVersion);
      
      console.log('⛔ Auto refresh after save has been disabled to prevent refresh loops');
    }
  };

  // Set up a real-time listener for storyProgress updates
  useEffect(() => {
    // Only set up listener if we have a bookId
    if (bookId) {
      console.log('🔄 Setting up real-time listener for storyProgress updates for bookId:', bookId);
      
      const db = getFirestore();
      const storyProgressRef = collection(db, 'storyProgress');
      
      // Create query for this book's storyProgress documents, ordered by newest first
      const progressQuery = query(storyProgressRef,
        where('bookId', '==', bookId),
        orderBy('createdAt', 'desc')
      );
      
      // Set up listener
      const unsubscribe = onSnapshot(progressQuery, (snapshot) => {
        if (!snapshot.empty) {
          console.log(`🔄 Real-time update: Detected ${snapshot.size} storyProgress documents`);
          
          // Check for changes - we're interested in added documents and pending→completed transitions
          const changes = snapshot.docChanges();

          // CRITICAL: Detect pending→completed transitions specifically
          let hasPendingToCompletedTransition = false;
          changes.forEach(change => {
            if (change.type === 'modified') {
              const data = change.doc.data();
              if (data.status === 'completed') {
                console.log('⚠️ IMPORTANT: Detected pending→completed transition:', change.doc.id);
                hasPendingToCompletedTransition = true;
              }
            }
          });
          
          // Also check for new completed documents as before
          const newDocs = changes.filter(change => change.type === 'added');
          const hasNewCompletedDocs = newDocs.some(change => {
            const data = change.doc.data();
            return data.status === 'completed';
          });
          
          // Update previewIds and availablePreviews
          // Only include completed, almostThere, and outlineGenerated versions in the preview list
          const validDocs = snapshot.docs.filter(doc => VALID_PREVIEW_STATUSES.includes(doc.data().status));
          const newPreviewIds = [];
          const docsByStatus = { completed: 0, pending: 0, other: 0 };
          
          validDocs.forEach((doc, i) => {
            const data = doc.data();
            const status = data.status || 'unknown';
            
            // Count by status
            if (status === 'completed') docsByStatus.completed++;
            else if (status === 'pending') docsByStatus.pending++;
            else docsByStatus.other++;
            
            newPreviewIds.push(doc.id);
          });
          
          console.log(`📓 Documents: ${docsByStatus.completed} completed, ${docsByStatus.pending} pending, ${docsByStatus.other} other (${snapshot.size - validDocs.length} excluded)`);
          
          // Set IDs for navigation
          setPreviewIds(newPreviewIds);
          setAvailablePreviews(validDocs.map(doc => doc.data()));
          
          // Handle ANY documents regardless of whether they're new or not
          // We want to ALWAYS show the latest content regardless of detection method
          console.log(`🔔 Processing ALL storyProgress documents, ${snapshot.size} total`);
          
          // Find NEWEST completed document regardless of whether it's new
          const newestCompletedDoc = snapshot.docs.find(doc => doc.data().status === 'completed');
          
          // CRITICAL: Check design mode and handle pending→completed transitions
          const isInDesignMode = designVersionShownRef.current === true;
          
          // Only update content if either we're not in design mode OR this is a pending→completed transition
          if (newestCompletedDoc && (!isInDesignMode || hasPendingToCompletedTransition)) {
            // If in design mode but updating due to pending→completed, log special case
            if (isInDesignMode && hasPendingToCompletedTransition) {
              console.log('🎨 SPECIAL CASE: Updating content while preserving design mode');
            }
            console.log('🔔 Found newest completed storyProgress document:', newestCompletedDoc.id);
            
            // Only update if design version isn't currently shown
            console.log(designVersionShownRef.current ? '🚧 Design version is shown - skipping storyProgress update' : '🎉 Loading newest storyProgress content');
            
            // Only proceed if design version is not being displayed
            const data = newestCompletedDoc.data();
            
            // Parse result if it's a string
            let resultData = data.result;
            if (typeof resultData === 'string') {
              try {
                resultData = JSON.parse(resultData);
              } catch (e) {
                console.error('Parse error:', e);
                // Try to salvage if the parsing failed
                if (typeof data.result === 'string' && data.result.includes('Pages')) {
                  console.log('Trying to extract pages from string directly');
                  try {
                    // Try to extract Pages array with regex
                    const pagesMatch = data.result.match(/"Pages":\s*\[(.*?)\]/s);
                    if (pagesMatch && pagesMatch[1]) {
                      const pagesContent = `[${pagesMatch[1]}]`;
                      console.log('Extracted pages JSON:', pagesContent);
                      resultData = { Pages: JSON.parse(pagesContent) };
                    }
                  } catch (innerError) {
                    console.error('Failed to extract pages from string:', innerError);
                  }
                }
              }
            }
            
            // Extract pages
            const pagesArray = resultData?.Pages || resultData?.['Pages:'] || [];
            console.log('DIRECT CONTENT UPDATE - Found pages:', pagesArray.length);
            
            // Create content array
            const newContent = Array(totalPages).fill('');
            pagesArray.forEach(page => {
              const pageNumber = page?.Page || page?.page;
              const pageContent = page?.Content || page?.content;
              
              if (pageNumber > 0 && pageNumber <= totalPages && pageContent) {
                newContent[pageNumber - 1] = pageContent;
                console.log(`Setting page ${pageNumber} content directly`);
              }
            });
            
            // If we have content, force an update immediately
            if (newContent.some(c => c)) {
              // DIRECTLY update content without ANY checks
              console.log('DIRECT UPDATE: Setting page contents with immediate update');
              
              try {
                // Create a shallow copy of the current window.currentBookData to modify
                // @ts-ignore - Using window.currentBookData which is set by other components
                if (window && window.currentBookData && typeof window.currentBookData === 'object') {
                  console.log('Updating window.currentBookData with new content');
                  // Create pages in the right format - simulate book structure for preview
                  const updatedPages = {};
                  newContent.forEach((content, idx) => {
                    if (content) {
                      const pageNum = idx + 1;
                      const spreadNum = Math.floor((pageNum + 1) / 2);
                      const spreadId = `spread-${spreadNum}`;
                      
                      // Initialize spread if it doesn't exist
                      if (!updatedPages[spreadId]) {
                        updatedPages[spreadId] = { elements: [] };
                      }
                      
                      // Add text element to the spread
                      updatedPages[spreadId].elements.push({
                        type: 'text',
                        content,
                        pageNumber: pageNum,
                        x: pageNum % 2 === 0 ? 650 : 40, // Right pages at x=650, left at x=40
                        y: 50,
                        width: 500,
                        height: 800
                      });
                    }
                  });
                  
                                  // DON'T update window.currentBookData - this makes the system think there's design content
                  // We want to keep storyProgress separate from design content
                  console.log('NOT updating window.currentBookData to avoid adding false design version');
                  /*
                  // @ts-ignore - Using window.currentBookData which is set by other components
                  window.currentBookData = {
                    // @ts-ignore - Using window.currentBookData which is set by other components
                    ...window.currentBookData,
                    spreads: {
                      // @ts-ignore - Using window.currentBookData which is set by other components
                      ...(window.currentBookData.spreads || {}),
                      ...updatedPages
                    },
                    previewContent: newContent
                  };
                  */
                }
              } catch (err) {
                console.error('Error updating currentBookData:', err);
              }
              
              // Simple approach - update all state in sequence
              console.log('Updating state directly');
              
              // First, update the content
              setLocalPageContents(newContent);
              
              // Notify parent component
              if (typeof onContentChange === 'function') {
                onContentChange(newContent);
              }
              
               // IMPORTANT: For pending→completed transitions, ALWAYS show the newly generated storyProgress
               // This ensures the version number is correct: (X-1) of X for the latest story
               if (hasPendingToCompletedTransition) {
                 console.log('⚠️ PENDING→COMPLETED: ALWAYS showing newly generated storyProgress with correct version');
                 // Force showing the latest storyProgress to ensure user sees the completed story
                 // and the version number displays correctly
                 designVersionShownRef.current = false;
                 setCurrentPreviewIndex(0);
               } else {
                 // Standard case - set to latest story version (index 0)
                 console.log('📄 Setting to latest storyProgress version');
                 setCurrentPreviewIndex(0);
               }
              
              setForceRerender(Date.now()); // Force rerender with timestamp
              setIsLoadingPreviews(false);
              setShowRefreshButton(false);
              
              // Force global rerender
              forceUpdate();
              
              console.log('🚨 FORCED CONTENT UPDATE COMPLETE');
              
              // Triple-check update with a small delay
              setTimeout(() => {
                console.log('Double-checking update...');
                
                // Force another rerender with a new timestamp
                const timestamp = Date.now();
                setForceRerender(timestamp);
                console.log(`Forced re-render at timestamp: ${timestamp}`);
                
                // And force another global update
                forceUpdate();
              }, 50);
            }
          }
        }
      }, (error) => {
        console.error('Error in storyProgress listener:', error);
      });
      
      // Store unsubscribe function to clean up on component unmount
      firestoreListenerRef.current = unsubscribe;
      
      // Clean up listener when component unmounts or bookId changes
      return () => {
        if (firestoreListenerRef.current) {
          console.log('🧹 Cleaning up storyProgress listener');
          firestoreListenerRef.current();
        }
      };
    }
  }, [bookId, hasDesignContent, processStoryProgressDocument]);


  // CRITICAL: Always check for design content at component mount and make sure it's prioritized
  // This effect will detect design content even before other initialization happens
  useEffect(() => {
    console.log(`🎨 MOUNT CHECK: Component mounted, bookId=${bookId}`);
    
    // Force a check for design content regardless of state
    const hasDesign = hasDesignContent();
    
    // Print extensive debug info to help diagnose loading issues
    console.log(`🎨 MOUNT DESIGN CHECK: hasDesign=${hasDesign}`);
    console.log(`🔴 CURRENT STATE: previewIds=${JSON.stringify(previewIds)}, currentIndex=${currentPreviewIndex}`);
    console.log(`🔴 DESIGN FLAG: designVersionShownRef.current=${designVersionShownRef.current}`);
    
    // ALWAYS prioritize design content regardless of other state
    if (hasDesign) {
      console.log('🎨 DESIGN EXISTS: GUARANTEED to show design content if available');
      
      // @ts-ignore - window.currentBookData is defined in the global scope
      const book = window.currentBookData || {};
      const hasBookSpreads = book.spreads && Object.keys(book.spreads).length > 0;
      
      console.log(`🎨 SPREADS CHECK: hasBookSpreads=${hasBookSpreads}, count=${hasBookSpreads ? Object.keys(book.spreads).length : 0}`);
      
      // ALWAYS force design display, regardless of other conditions
      if (hasBookSpreads) {
        console.log('💯 FORCING DESIGN MODE: Setting design as initial view');
        
        // Set both flags to ensure design mode
        designVersionShownRef.current = true;
        setCurrentPreviewIndex(previewIds.length);
        
        // Force rerender with delay to ensure state updates are applied
        setTimeout(() => {
          setForceRerender(prev => prev + 1);
        }, 50);
      }
    }
  }, [hasDesignContent, bookId, previewIds.length]); // Rerun when these dependencies change

  // One-time initial load of content directly from Firestore or design
  useEffect(() => {
    if (ALLOW_ONE_TIME_INITIAL_LOAD && bookId && !initialLoadRef.current) {
      console.log('🔄 Performing one-time initial preview load for bookId:', bookId);
      initialLoadRef.current = true;
      
      setIsLoadingPreviews(true);
      
      // Main function to load all content and set up versions
      const loadAllContent = async () => {
        try {
          // SUPER AGGRESSIVE DESIGN DETECTION
          // GUARANTEED to always check for design content and show it if available
          // @ts-ignore - window.currentBookData is defined in the global scope
          const bookData = window.currentBookData || {};
          const hasBookSpreads = bookData.spreads && Object.keys(bookData.spreads).length > 0;
          
          console.log(`🎨 INITIAL LOAD: Checking for design content, hasBookSpreads=${hasBookSpreads}`);
          
          // First, check if design content exists and ALWAYS show it if available
          if (hasBookSpreads) {
            // Do a thorough check for actual text content in spreads
            let hasDesignText = false;
            let hasSpreadText = false;
            
            // Log all available spreads to debug
            console.log('📄 Available spreads:', Object.keys(bookData.spreads).join(', '));
            
            // Check each spread for content
            for (const spreadId of Object.keys(bookData.spreads)) {
              if (spreadId === 'spread-0') continue; // Skip cover
              
              console.log(`📑 Examining spread ${spreadId}...`); 
              const elements = bookData.spreads[spreadId]?.elements || [];
              console.log(`📑 Found ${elements.length} elements in spread ${spreadId}`);
              
              // Look for text elements with actual content
              for (const element of elements) {
                if (element?.type === 'text') {
                  hasSpreadText = true;
                  if (typeof element.content === 'string' && element.content.trim().length > 0) {
                    hasDesignText = true;
                    console.log(`📝 Found text content in ${spreadId}:`, element.content.substring(0, 30) + '...');
                    break;
                  }
                }
              }
              if (hasDesignText) break;
            }
            
            console.log(`🎨 DESIGN CHECK: hasSpread=${hasBookSpreads}, hasSpreadText=${hasSpreadText}, hasDesignText=${hasDesignText}`);
            
            // ALWAYS SHOW DESIGN CONTENT when it exists, no exceptions
            if (hasDesignText) {              
              console.log('🎨 ALWAYS SHOW DESIGN: Found design with text content - FORCING design mode');
              
              // CRITICAL: Set design mode flags immediately
              designVersionShownRef.current = true;
              
              // Force currentPreviewIndex to indicate design version
              setCurrentPreviewIndex(previewIds.length);
              
              // Load design content by extracting from book data
              console.log('🎨 EXTRACTING DESIGN: Getting content from currentBookData');
              const extractedContent = Array(totalPages).fill('');
              
              for (const spreadId of Object.keys(bookData.spreads)) {
                if (spreadId === 'spread-0') continue;
                
                const spreadNum = parseInt(spreadId.split('-')[1]);
                const elements = bookData.spreads[spreadId]?.elements || [];
                
                for (const element of elements) {
                  if (element?.type === 'text' && typeof element.content === 'string' && element.content.trim().length > 0) {
                    const pageIndex = (element.pageNumber ? element.pageNumber - 1 : spreadNum - 1);
                    
                    if (pageIndex >= 0 && pageIndex < totalPages) {
                      // Store as array of elements instead of concatenated string
                      if (!Array.isArray(extractedContent[pageIndex])) {
                        extractedContent[pageIndex] = extractedContent[pageIndex] ? [{ content: extractedContent[pageIndex] }] : [];
                      }
                      extractedContent[pageIndex].push({ content: element.content });
                      console.log(`📄 Setting page ${pageIndex + 1} content from design`);
                    }
                  } else if (element?.type === 'image') {
                    // Handle image elements
                    const pageIndex = (element.pageNumber ? element.pageNumber - 1 : spreadNum - 1);
                    
                    if (pageIndex >= 0 && pageIndex < totalPages) {
                      // Initialize page content if needed
                      if (!Array.isArray(extractedContent[pageIndex])) {
                        extractedContent[pageIndex] = [];
                      }
                      // Add image element
                      extractedContent[pageIndex].push({ type: 'image', src: element.src, imagePrompt: element.imagePrompt });
                      console.log(`📸 Setting image on page ${pageIndex + 1} from design`);
                    }
                  }
                }
              }
              
              // Apply the extracted content immediately
              if (extractedContent.some(c => c)) {
                console.log('💡 SETTING DESIGN CONTENT: Immediate update');
                setLocalPageContents(extractedContent);
                if (typeof onContentChange === 'function') {
                  onContentChange(extractedContent);
                }
                
                // Triple-check to force version display update
                setTimeout(() => {
                  console.log('💯 DESIGN MODE REINFORCED: Ensuring design mode flags are set');
                  designVersionShownRef.current = true;
                  setCurrentPreviewIndex(previewIds.length);
                  setForceRerender(prev => prev + 1); // Force rerender to ensure UI updates
                }, 10);
                
                setIsLoadingPreviews(false);
                return; // Skip other content loading paths
              }
            }
          }
          
          // FALLBACK: If no design content found or extraction failed, load storyProgress
          console.log('📘 No design found or extraction failed - loading storyProgress');
          designVersionShownRef.current = false;
          setCurrentPreviewIndex(0);
          setIsLoadingPreviews(false);
        } catch (error) {
          console.error('❌ Error loading content:', error);
          setIsLoadingPreviews(false);
          setShowRefreshButton(true);
        }
      };
      
      // Start loading content
      loadAllContent();
      
      // Set a timeout to prevent hanging on loading
      setTimeout(() => {
        console.log('⏱️ Force render timeout triggered');
        setForceRenderTimeout(true);
        setIsLoadingPreviews(false);
      }, 5000);
    }
  }, [bookId, totalPages, onContentChange, previewIds.length]);
  

  // Function to navigate between preview versions
  const navigatePreview = async (newIndex) => {
    if (newIndex < 0 || newIndex >= previewIds.length) {
      console.error(`🚫 Invalid preview index: ${newIndex}`);
      return;
    }
    
    // Calculate the version number (for user-facing messages)
    const versionNumber = previewIds.length - newIndex; // Reverse index for version number
    console.log(`🔄 Navigating to preview index ${newIndex} (version ${versionNumber} of ${previewIds.length})`);
    
    setIsLoadingPreviews(true);
    setIsClearing(true);
    
    try {
      console.log('🔄 Navigation attempt starting - clearing current content first');

      // Clear content temporarily
      const emptyContents = Array(totalPages).fill("");
      setLocalPageContents(emptyContents);
      if (typeof onContentChange === 'function') {
        onContentChange(emptyContents);
      }
      
      // Update current index
      setCurrentPreviewIndex(newIndex);
      
      // Fetch the specific preview content
      const db = getFirestore();
      const storyProgressRef = collection(db, 'storyProgress');
      
      const q = query(storyProgressRef,
        where('bookId', '==', bookId),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      // Keep indices aligned with previewIds by filtering to valid preview statuses
      const docs = snapshot.docs.filter(doc => VALID_PREVIEW_STATUSES.includes(doc.data().status));
      
      // Skip to the document at the newIndex position
      if (docs.length > newIndex) {
        const doc = docs[newIndex];
        const data = doc.data();
        
        console.log(`📄 Loading storyProgress document: id=${doc.id}, status=${data.status || 'unknown'}`);
        
        // Parse result if it's a string
        let resultData = data.result;
        if (typeof resultData === 'string') {
          try {
            resultData = JSON.parse(resultData);
            console.log('✅ Successfully parsed result string into object');
          } catch (e) {
            console.error('❌ Parse error:', e);
            // If there's a parse error, try to salvage what we can
            console.log('🔄 Attempting to handle result as raw string');
            // Continue with the original string - it might still have usable content
            resultData = { raw: data.result };
          }
        } else {
          console.log('ℹ️ Result was already an object, no parsing needed');
        }
        
        // Extract pages with more flexible fallbacks
        const pagesArray = resultData?.Pages || resultData?.['Pages:'] || 
                           // Additional fallbacks for nested structures
                           (resultData?.result?.Pages || resultData?.result?.['Pages:'] || 
                           resultData?.data?.Pages || resultData?.data?.['Pages:'] || []);
        
        console.log(`🔍 Found pages array with ${pagesArray.length || 0} items`);
        
        // Create content array
        const newContent = Array(totalPages).fill('');
        
        // If we found pages, process them
        if (Array.isArray(pagesArray) && pagesArray.length > 0) {
          pagesArray.forEach(page => {
            const pageNumber = page?.Page || page?.page;
            const pageContent = page?.Content || page?.content;
            
            if (pageNumber > 0 && pageNumber <= totalPages && pageContent) {
              console.log(`📝 Setting content for page ${pageNumber}`);
              newContent[pageNumber - 1] = pageContent;
            }
          });
        } else {
          // If no pages array found, try to extract from raw content if it exists
          console.warn('⚠️ No pages array found, trying alternative extraction');
          
          // Handle special case where we might have a raw string
          if (typeof resultData?.raw === 'string' && resultData.raw.includes('"Pages"')) {
            try {
              // Try to extract Pages array with regex
              const pagesMatch = resultData.raw.match(/"Pages":\s*\[(.*?)\]/s);
              if (pagesMatch && pagesMatch[1]) {
                const pagesContent = `[${pagesMatch[1]}]`;
                console.log('📑 Extracted pages JSON from raw string');
                const extractedPages = JSON.parse(pagesContent);
                
                extractedPages.forEach(page => {
                  const pageNumber = page?.Page || page?.page;
                  const pageContent = page?.Content || page?.content;
                  
                  if (pageNumber > 0 && pageNumber <= totalPages && pageContent) {
                    console.log(`📝 Setting content for page ${pageNumber} from extracted data`);
                    newContent[pageNumber - 1] = pageContent;
                  }
                });
              }
            } catch (e) {
              console.error('❌ Failed to extract pages from raw string:', e);
            }
          }
        }
        
        // StoryProgress content is TEXT ONLY - no design influence
        // Image elements are only shown when viewing design version
        
        if (newContent.some(c => c)) {
          console.log('✅ Found valid content, updating state');
          setLocalPageContents(newContent);
          
          // Force rerender without component remounting
          setForceRerender(prev => prev + 1);
        } else {
          console.warn('⚠️ No valid content found in this version');
          notify.warning('No content found in this version', { toastId: 'preview-no-content' });
        }
      }
    } catch (error) {
      console.error('Navigation error:', error);
      notify.error('Failed to load selected version', { toastId: 'preview-load-error' });
    } finally {
      setIsLoadingPreviews(false);
      setIsClearing(false);
    }
  };

  // Handle manual refresh button click
  const handleManualRefresh = async () => {
    try {
      setRefreshing(true);
      
      const db = getFirestore();
      const storyProgressRef = collection(db, 'storyProgress');
      
      // Try to get the newest valid storyProgress document (skipping cancelled/failed/error)
      const refreshQuery = query(storyProgressRef,
        where('bookId', '==', bookId),
        orderBy('createdAt', 'desc')
      );
      
      console.log('🔄 Manual refresh: Querying storyProgress for bookId:', bookId);
      const refreshSnapshot = await getDocs(refreshQuery);
      const doc = refreshSnapshot.docs.find(d => VALID_PREVIEW_STATUSES.includes(d.data().status));
      
      if (!doc) {
        console.log('❌ Manual refresh: No valid storyProgress documents found');
        notify.error('No story content found', { toastId: 'preview-no-story' });
        setRefreshing(false);
        return;
      }
      
      // Process the document
      const data = doc.data();
      
      console.log('🎉 Manual refresh: Found document, status:', data.status);
      
      // Parse result
      let resultData = data.result;
      if (typeof resultData === 'string') {
        try {
          resultData = JSON.parse(resultData);
        } catch (e) {
          console.error('Parse error:', e);
        }
      }
      
      // Extract pages
      const pagesArray = resultData?.Pages || resultData?.['Pages:'] || [];
      
      // Create content array
      const newContent = Array(totalPages).fill('');
      pagesArray.forEach(page => {
        const pageNumber = page?.Page || page?.page;
        const pageContent = page?.Content || page?.content;
        
        if (pageNumber > 0 && pageNumber <= totalPages && pageContent) {
          newContent[pageNumber - 1] = pageContent;
        }
      });
      
      if (newContent.some(c => c)) {
        setLocalPageContents(newContent);
        if (typeof onContentChange === 'function') {
          onContentChange(newContent);
        }
        notify.success('Content loaded successfully', { toastId: 'preview-refresh-success' });
        setShowRefreshButton(false);
      } else {
        notify.warning('Found document but no content', { toastId: 'preview-no-content-found' });
      }
    } catch (error) {
      console.error('Manual refresh error:', error);
      notify.error('Error refreshing content', { toastId: 'preview-refresh-error' });
    } finally {
      setRefreshing(false);
    }
  };
  
  // Determine if we should show content or a loading state
  const showLoading = isLoadingPreviews && !forceRenderTimeout;
  
  // Determine what content to display
  const contentToDisplay = useCallback(() => {
    // Check if design content exists
    const hasDesign = hasDesignContent();
    
    // CRITICAL: Only show design if user is on the design version
    // Design version is AFTER all storyProgress versions (currentPreviewIndex >= previewIds.length)
    const isOnDesignVersion = Number(currentPreviewIndex) >= Number(previewIds.length) && hasDesign;

    console.log('IS ON DESIGN VERSION:', isOnDesignVersion, 'currentPreviewIndex:', currentPreviewIndex, 'previewIds.length:', previewIds.length,hasDesign)
    
    // ONLY show design when explicitly viewing the design version
    if (hasDesign && isOnDesignVersion) {
      // @ts-ignore - window.currentBookData is defined in the global scope
      const book = window.currentBookData || {};
      console.log('🎨 DESIGN CONTENT: Showing design version (index:', currentPreviewIndex, 'of', previewIds.length, ')');
      
      // Use dedicated design extraction module
      const designContent = extractDesignContent(book, totalPages);
      if (designContent) {
        return designContent;
      }
    } 
    
    // If user is viewing storyProgress versions, show storyProgress content
    if (currentPreviewIndex < previewIds.length) {
      console.log('� STORYPROGRESS CONTENT: Showing storyProgress version', currentPreviewIndex + 1, 'of', previewIds.length);
    }

    // Otherwise fall back to standard content logic
    // Add defensive check to ensure localPageContents is an array before using .some()
    console.log('CONTENT TYPE CHECK:', { 
      type: typeof localPageContents, 
      isArray: Array.isArray(localPageContents),
      value: localPageContents
    });
    
    // If we have local page contents, use those
    if (Array.isArray(localPageContents) && localPageContents.some(c => c)) {
      return [...localPageContents];
    }
    
    // If no local content but we have drafted result, show that
    if (draftedResult) {
      const draftedText = typeof draftedResult === 'string' ? draftedResult : JSON.stringify(draftedResult, null, 2);
      return Array(totalPages).fill(draftedText);
    }
    
    // Otherwise no real content exists; render as empty and let the empty-state UI handle the message
    return new Array(totalPages).fill('');
  }, [localPageContents, totalPages, currentPreviewIndex, previewIds.length, draftedResult]);
  
  // Check if we have content to show
  const hasContent = useMemo(() => {
    const content = contentToDisplay();
    // DEFENSIVE: Ensure content is array before checking
    const hasValidContent = Array.isArray(content) && content.some(c => {
      if (!c) return false;
      // Handle both string content and array-of-elements content
      if (typeof c === 'string') {
        return !c.includes("Preview content is not yet available");
      } else if (Array.isArray(c)) {
        return c.length > 0;
      }
      return false;
    });
    console.log(`Content check: hasValidContent=${hasValidContent}, forceRerender=${forceRerender}`);
    return hasValidContent;
  }, [contentToDisplay, forceRerender]);
  
  // Update refresh button visibility based on content
  useEffect(() => {
    setShowRefreshButton(!hasContent);
    console.log(`Refresh button visibility updated: ${!hasContent}, hasContent=${hasContent}`);
  }, [hasContent]);
  
  // Debug logging for content changes
  useEffect(() => {
    console.log(`LocalPageContents updated (length: ${localPageContents.length}):`, localPageContents);
  }, [localPageContents]);
  
  
  // Calculate total versions count (storyProgress + design if exists)
  const totalVersionsCount = useCallback(() => {
    // Get storyProgress versions count
    const storyProgressCount = Array.isArray(previewIds) ? previewIds.length : 0;
    
    // Debug the actual previewIds array to diagnose issues
    console.log('🔢 DEBUG: PreviewIds:', previewIds);
    console.log(`🔢 DEBUG: PreviewIds length: ${storyProgressCount}`);
    
    // Check for design content - explicitly call the function to avoid any caching issues
    // IMPORTANT: Force check for design content directly without caching
    const book = window.currentBookData || {};
    const hasSpreads = book.spreads && Object.keys(book.spreads).length > 0;
    let hasDesignText = false;
    
    if (hasSpreads) {
      // Check for text content in spreads
      for (const spreadId of Object.keys(book.spreads)) {
        if (spreadId === 'spread-0') continue;
        const elements = book.spreads[spreadId]?.elements || [];
        for (const element of elements) {
          if (element?.type === 'text' && typeof element.content === 'string' && element.content.trim().length > 0) {
            hasDesignText = true;
            break;
          }
        }
        if (hasDesignText) break;
      }
    }
    
    console.log(`🔴 DESIGN CHECK DEBUG: Direct check: hasSpreads=${hasSpreads}, hasDesignText=${hasDesignText}`);
    
    const actualDesignExists = hasDesignText; // Using direct check instead of function call
    
    // Calculate total - only add design if it exists
    let count = storyProgressCount;
    if (actualDesignExists) {
      count += 1;
      console.log('🔢 DESIGN EXISTS: Adding +1 to version count');
    } else {
      console.log('🔢 NO DESIGN: Version count equals only storyProgress count');
    }
    
    // Ensure we always have at least 1 version if there's any content
    count = Math.max(count, hasContent ? 1 : 0);
    
    console.log(`🔢 Version count calculation: ${storyProgressCount} storyProgress + ${actualDesignExists ? '1' : '0'} design = ${count} total`);
    return count;
  }, [previewIds, hasDesignContent, hasContent]);
  
  // Calculate current version number (accounting for design version)
  const currentVersionNumber = useCallback(() => {
    // First check if we have design content
    const hasDesign = hasDesignContent();
    // Get total count (includes design if it exists)
    const totalCount = totalVersionsCount();
    
    // CRITICAL: Check if showing design by checking flag
    const isShowingDesignVersion = designVersionShownRef.current === true;
    
    // Calculate version numbers differently depending on whether design exists and what we're showing
    if (isShowingDesignVersion) {
      // When showing design, it's always the LAST version (highest number = totalCount)
      const versionNum = totalCount;
      console.log(`🔢 Current version is DESIGN: ${versionNum} of ${totalCount}`);
      return versionNum;
    } else {
      // For storyProgress versions, we need to account for design differently
      // First calculate position within storyProgress (0 = newest, higher = older)
      const storyProgressPosition = currentPreviewIndex;
      
      // Now calculate version number (1-indexed, where 1 = oldest)
      let storyProgressNum;
      
      if (hasDesign) {
        // CRITICAL FIX: When design exists, storyProgress is numbered starting from totalCount-1
        // This ensures: Design = N/N, Latest storyProgress = N-1/N, etc.
        storyProgressNum = totalCount - storyProgressPosition - 1;
        console.log(`🔢 With design: storyProgress at index ${storyProgressPosition} is version ${storyProgressNum} of ${totalCount}`);
      } else {
        // No design - numbering is straightforward: newest = totalCount, oldest = 1
        storyProgressNum = totalCount - storyProgressPosition;
        console.log(`🔢 No design: storyProgress at index ${storyProgressPosition} is version ${storyProgressNum} of ${totalCount}`);
      }
      
      return storyProgressNum;
    }
  }, [currentPreviewIndex, previewIds.length, totalVersionsCount, hasDesignContent, designVersionShownRef]);
  
  // Debug log version counts on changes
  useEffect(() => {
    try {
      const actualDesignExists = hasDesignContent();
      const versionCount = totalVersionsCount();
      const currentVersion = currentVersionNumber();
      
      // More detailed debugging to help diagnose navigation issues
      console.log(`🔢 VERSION DISPLAY UPDATE: Version ${currentVersion} of ${versionCount}`);
      console.log(`📎 DOCUMENT STATUS: ${previewIds.length} storyProgress documents, hasDesign=${actualDesignExists}, currentIndex=${currentPreviewIndex}`);
      console.log(`📃 NAVIGATION STATUS: Can go older=${currentPreviewIndex < previewIds.length - 1}, Can go newer=${currentPreviewIndex > 0 || (currentPreviewIndex === 0 && actualDesignExists)}`);
    } catch (err) {
      console.error('Error in version display effect:', err);
    }
  }, [totalVersionsCount, currentVersionNumber, previewIds, hasDesignContent, currentPreviewIndex]);
  
  // Single, friendly empty state shown when no preview content exists
  const renderEmptyState = () => (
    <div className="flex flex-col items-center justify-center h-[400px] text-center text-gray-500 px-6">
      {isProcessing ? (
        <>
          <svg className="animate-spin h-10 w-10 text-indigo-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <h3 className="text-lg font-semibold text-gray-900">Your preview is generating...</h3>
          <p className="mt-2 max-w-sm">Please wait while we create your story preview.</p>
        </>
      ) : showLoading ? (
        <>
          <svg className="animate-spin h-10 w-10 text-indigo-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <h3 className="text-lg font-semibold text-gray-900">Loading preview...</h3>
        </>
      ) : (
        <>
          <svg className="h-12 w-12 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
          </svg>
          <h3 className="text-lg font-semibold text-gray-900">No preview yet</h3>
          <p className="mt-2 max-w-sm">Fill in your story details and click "Generate Story Preview" to see your pages here.</p>
        </>
      )}
    </div>
  );
  
  return (
    <div className="space-y-6">
      {/* Header with title and version navigation */}
      <div className="flex justify-between items-center bg-white px-6 py-4 rounded-t-xl shadow-sm">
        <div className="flex items-center space-x-2">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            Preview
            {hasContent && (
              <span className="ml-2 text-sm bg-indigo-100 text-indigo-800 py-1 px-2 rounded-full">
                Version {currentVersionNumber()} of {totalVersionsCount()}
              </span>
            )}
          </h2>
        </div>
        
        {/* Version navigation controls */}
        {totalVersionsCount() > 1 && (
          <div className="flex space-x-2">
            <button
              onClick={() => {
                console.log('🔄 Previous button clicked - FIXED APPROACH');
                
                // If showing design version
                if (designVersionShownRef.current === true) {
                  console.log('🔄 From design to storyProgress');
                  
                  // Reset the design flag
                  designVersionShownRef.current = false;
                  
                  // Update UI
                  setIsLoadingPreviews(true);
                  setIsClearing(true);
                  
                  // Create empty content while loading
                  const emptyContent = Array(totalPages).fill('');
                  setLocalPageContents(emptyContent);
                  
                  // Set to storyProgress index 0 (latest)
                  setCurrentPreviewIndex(0);
                  
                  // Directly try to navigate
                  try {
                    // If there are storyProgress versions, navigate to index 0 (newest)
                    if (previewIds.length > 0) {
                      navigatePreview(0);
                    } else {
                      console.log('⚠️ No storyProgress versions available');
                      setIsLoadingPreviews(false);
                      setIsClearing(false);
                    }
                  } catch (err) {
                    console.error('❌ Error navigating to storyProgress:', err);
                    setIsLoadingPreviews(false);
                    setIsClearing(false);
                  }
                } 
                // Normal navigation between storyProgress versions
                else if (currentPreviewIndex < previewIds.length - 1) {
                  console.log(`🔄 From index ${currentPreviewIndex} to ${currentPreviewIndex + 1}`);
                  navigatePreview(currentPreviewIndex + 1);
                } 
                // Already at oldest version
                else {
                  console.log('🚫 Already at oldest version');
                }
              }}
              disabled={false} /* Always enabled when showing design version */
              className={`p-2 rounded-full ${previewIds.length === 0 && currentPreviewIndex < previewIds.length && currentPreviewIndex >= previewIds.length - 1
                      ? 'text-gray-400' : 'text-indigo-600 hover:bg-indigo-50'}`}
              aria-label="Previous version"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={() => {
                console.log('🔄 Next (newer) version button clicked');
                // If we're at the newest storyProgress (index 0) and design exists
                if (currentPreviewIndex === 0 && hasDesignContent()) {
                  console.log('🔄 From newest storyProgress to design version');
                  showDesignVersion();
                } else if (currentPreviewIndex > 0 && currentPreviewIndex < previewIds.length) {
                  console.log(`🔄 From index ${currentPreviewIndex} to ${currentPreviewIndex - 1}`);
                  // Navigate to newer storyProgress (lower index = newer in our sorting)
                  navigatePreview(currentPreviewIndex - 1);
                } else {
                  console.log('🚫 Already at newest version or design version');
                }
              }}
              disabled={previewIds.length === 0 || 
                      (currentPreviewIndex === 0 && !(window.currentBookData && Object.keys(window.currentBookData.spreads || {}).length > 0)) || 
                      (currentPreviewIndex >= previewIds.length && designVersionShownRef.current)}
              className={`p-2 rounded-full ${(previewIds.length === 0 || 
                      (currentPreviewIndex === 0 && !(window.currentBookData && Object.keys(window.currentBookData.spreads || {}).length > 0)) || 
                      (currentPreviewIndex >= previewIds.length && designVersionShownRef.current))
                      ? 'text-gray-400' : 'text-indigo-600 hover:bg-indigo-50'}`}
              aria-label="Next version"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </div>
      
      {/* Preview content area */}
      <div className="bg-white rounded-lg" style={{
        minHeight: '500px',
        backgroundColor: isEditMode ? '#f5f7f9' : 'white',
        padding: isEditMode ? '16px' : '0',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        height: isEditMode ? '500px' : 'auto',
        overflow: isEditMode ? 'auto' : 'visible'
      }}>
        {isEditMode ? (
          <div>
         
              <PreviewToolbar
                hasDesignContent={hasDesignContent()}
                isDesignVersion={currentPreviewIndex >= previewIds.length && hasDesignContent()}
                onShowDesign={() => {
                  console.log('Design button clicked in PreviewToolbar');
                  navigate(`/design/${bookId}`);
                }}
              />
        

            {hasContent ? (
              showLoading ? (
                <div className="flex items-center justify-center h-[500px] text-gray-500">
                  Loading preview...
                </div>
              ) : (
                <MessengerScrollablePreview
                  key={`messenger-preview-${designVersionShownRef.current ? 'design' : currentPreviewIndex}-${forceRerender}`}
                  totalPages={totalPages}
                  pageContents={contentToDisplay()} // Use contentToDisplay to respect design vs story content
                  onContentChange={handleContentChange}
                  step={step}
                  designVersionShownRef={designVersionShownRef}
                />
              )
            ) : renderEmptyState()}
          </div>
        ) : (
          // Keep scroll only for non-edit mode
          <div className="overflow-y-auto" style={{ maxHeight: '500px' }}>
            {hasContent ? (
              <PreviewContent
                key={`preview-${currentPreviewIndex}-${forceRerender}`}
                currentPage={currentPage}
                totalPages={totalPages}
                pageContents={isClearing ? new Array(totalPages).fill("") : contentToDisplay()}
                onContentChange={handleContentChange}
                step={step}
                isClearing={isClearing}
              />
            ) : renderEmptyState()}
          </div>
        )}
      </div>

      {!designVersionShownRef.current && (
        <button
          onClick={handleSaveClick}
          className="w-full bg-indigo-600 text-white py-3 px-4 rounded-md hover:bg-indigo-700 transition-colors"
        >
          Save and Continue to Design
        </button>
      )}

      {/* Confirmation Modal */}
      {onSaveBasicInfo && onContinueWithoutSave && (
        <ConfirmationModal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onSaveBasic={() => {
            setShowConfirmModal(false);
            if (onSaveBasicInfo) {
              onSaveBasicInfo();
            }
          }}
          onSavePreview={() => {
            setShowConfirmModal(false);
            if (onSavePreview) {
              // Determine if we are currently viewing the design version
              const isOnDesignVersion = currentPreviewIndex >= previewIds.length && hasDesignContent();
              // Pass the currently selected content AND the design mode flag to onSavePreview
              onSavePreview(localPageContents, isOnDesignVersion);
            }
          }}
          onContinueWithoutSave={() => {
            setShowConfirmModal(false);
            if (onContinueWithoutSave) {
              onContinueWithoutSave();
            }
          }}
          isProcessing={isProcessing}
          operationType={operationType}
          title="Save Changes?"
          message="Choose how you would like to proceed with your changes:"
        />
      )}
    </div>
  );
}
